import TitlePage from 'components/atoms/title-page'
import React, { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router'
import CustomersApiV2 from 'api/v2/admins/customers-api-v2'
import UsersApiV2 from 'api/v2/admins/users-api-v2'
import CustomerOrdersApiV2 from 'api/v2/admins/customer-orders-api-v2'
import CustomerCheckoutSection from 'domains/customer-checkout/organisms/section'
import CustomerAddressCard from 'domains/customer/organisms/address-card'
import UserAddressModal from 'domains/user/organisms/address-modal'
import CheckoutShipmentSection from 'domains/customer-checkout/organisms/shipment-section'
import CustomercCheckoutPaymentOptions from 'domains/customer-checkout/organisms/payment-options'
import CustomerCheckoutPaymentMethodCard from 'domains/customer-checkout/organisms/payment-method-card'
import CustomerCheckoutVoucherModal from 'domains/voucher/organisms/voucher-modal'
import CustomerCheckoutTotalSection from 'domains/customer-checkout/organisms/total-section'
import CustomerCheckoutsService from 'domains/customer-checkout/services/customer-checkout-checkout-service'
import CustomerCartVoucherService from 'domains/customer-checkout/services/customer-checkout-cart-voucher-service'
import { Alert, Card, Row, Col, Modal, ModalBody, Button } from 'reactstrap'
import LoaderButton from 'components/molecules/loader-button'
import { useHistory } from 'react-router-dom'
import { ALERT_TYPES } from 'constants/alert-constants'
import { WEB_URL_CONSTANTS } from 'constants/web-url-constants'
import PropTypes from 'prop-types'
import AddressService from 'domains/address/address-service'
import CheckoutTotalService from 'domains/customer-checkout/services/customer-checkout-total-service'
import CustomerCheckoutAffiliateNameField from 'domains/customer-checkout/organisms/affiliate-name-field'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const DEFAULT_PAYMENT_OPTIONS = {
  suggestions: [ {
    label           : 'Most Used Payment Method',
    payment_methods : []
  } ],
  categories: [ {
    label           : 'Default Method',
    payment_methods : []
  } ],
  is_payment_method_limited: false
}

const CustomerCheckoutOrderCreatePage = ({ pageUtils }) => {
  const [ selectedAddress, setSelectedAddress ]               = useState(undefined)
  const [ isAddressLoading, setIsAddressLoading ]             = useState(true)
  const [ isAddressModalOpen, setIsAddressModalOpen ]         = useState(false)
  const [ isOrderProcessing, setIsOrderProcessing ]           = useState(false)
  const [ selectedPaymentMethod, setSelectedPaymentMethod ]   = useState(undefined)
  const [ isPaymentModalOpen, setIsPaymentModalOpen ]         = useState(false)
  const [ selectedShipmentMethod, setSelectedShipmentMethod ] = useState(null)
  const [ voucher, setVoucher ]                               = useState(undefined)
  const [ voucherDiscount, setVoucherDiscount ]               = useState(undefined)
  const [ paymentOptions, setPaymentOptions ]                 = useState(DEFAULT_PAYMENT_OPTIONS)
  const [ shipmentOptions, setShipmentOptions ]               = useState(null)
  const [ selectedAffiliateName, setSelectedAffiliateName ]   = useState(undefined)
  const [ isShipmentLoading, setIsShipmentLoading ]           = useState(true)

  const { id }      = useParams()
  const history     = useHistory({})
  const location    = useLocation()
  const cartItemIds = location.state.cartItemIds

  const fetchCustomer = async () => {
    await CustomersApiV2.show(id)
      .then((response) => {
        const customer = response.data.customer
        setUserId(customer.user_id)
      })
  }

  // Address States
  const [ userId, setUserId ] = useState(null)
  useEffect(() => {
    // Fetch address first to set default address to state
    fetchCustomer()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ id ])

  const [ addresses, setAddresses ] = useState([])
  useEffect(() => {
    // Fetch address first to set default address to state
    if (userId) fetchAddresses()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ userId ])

  const fetchAddresses = async () => {
    await UsersApiV2.getAddresses({ userId: userId })
      .then((response) => {
        setIsAddressLoading(true)
        const addresses = response.data.addresses

        let defaultAddress = addresses.find((address) => address.is_customer_default  == true)
        defaultAddress     = defaultAddress || addresses[0]

        const isAddressInvalid = AddressService.isInvalid(defaultAddress)

        if (isAddressInvalid) {
          alert('Please update your selected address first before trying to checkout')
        } else {
          setAddresses(addresses)
          setSelectedAddress(defaultAddress)
        }
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsAddressLoading(false))
  }

  // Update shipment options and payment methods if selectedAddress changes
  useEffect(() => {
    if (selectedAddress) {
      const isAddressInvalid = AddressService.isInvalid(selectedAddress)
      if (isAddressInvalid) {
        alert('Please update your selected address first before trying to checkout')
      } else {
        fetchShipmentOptions(id, selectedAddress.id)
        fetchPaymentOptions(selectedAddress.id)
      }
    }
  }, [ selectedAddress ])

  const fetchShipmentOptions = async (id, shippingAddressId) => {
    setIsShipmentLoading(true)

    await CustomerOrdersApiV2.postShipmentOptions(id, shippingAddressId, cartItemIds)
      .then((response) => {
        const shipmentOptions = response.data.shipment_options
        setShipmentOptions(shipmentOptions)
        setIsShipmentLoading(false)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
      .finally(() => setIsShipmentLoading(false))

  }

  const fetchPaymentOptions = async (shippingAddressId) => {
    await CustomerOrdersApiV2.getPaymentOptions(shippingAddressId)
      .then((response) => {
        const paymentOptions = response.data.payment_options
        setPaymentOptions(paymentOptions)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
  }

  const handleSelectAddress = (selectedAddress) => {
    setSelectedAddress(selectedAddress)
    setIsAddressModalOpen(false)
  }

  const [ checkoutOrder, setCheckoutOrder ] = useState(null)

  // useEffect to monitor changes between selected shipment method, payment method and discount
  // change order total attribute
  useEffect(() => {
    if (shipmentOptions) {
      let newCheckoutOrder = CheckoutTotalService.transformToOrder({
        shipment         : shipmentOptions,
        selectedPayment  : selectedPaymentMethod,
        selectedShipment : selectedShipmentMethod,
        voucherDiscount  : voucherDiscount
      })
      setCheckoutOrder(newCheckoutOrder)
    }

  }, [ selectedPaymentMethod?.id, selectedShipmentMethod, voucherDiscount ])

  /**
   * Clicking on Shipment Method for a single Shipment
   * @param {Object} selectedShipMethod
   * @param {Integer} index of selected shipment method in state array
   */
  const handleClickShippingMethod = (selectedShipMethod) => setSelectedShipmentMethod(selectedShipMethod)

  const handleSelectPayment = (selectedPayment) => {
    setIsPaymentModalOpen(false)
    setSelectedPaymentMethod(selectedPayment)
  }

  // handler when click checkbox insurance
  const handleClickInsuranceCheckbox = () => {
    const shipmentMethod    = selectedShipmentMethod
    const insurance         = shipmentMethod?.is_insurance || false
    const newShipmentMethod = {
      ...shipmentMethod,
      is_insurance: !insurance
    }
    setSelectedShipmentMethod(newShipmentMethod)
  }

  // Create Order with given states
  const handleClickCreateOrder = async () => {
    setIsOrderProcessing(true)
    const errorMsg = CustomerCheckoutsService.validate(selectedPaymentMethod, selectedShipmentMethod, checkoutOrder)

    if (errorMsg) {
      alert(errorMsg)
      setIsOrderProcessing(false)
      return
    }

    const shipment = CustomerCheckoutsService.generateShipmentModel(shipmentOptions, selectedShipmentMethod)

    await CustomerOrdersApiV2.postOrder(
      id,
      selectedAddress,
      voucher ? voucher.code : '',
      selectedPaymentMethod,
      shipment,
      cartItemIds,
      selectedAffiliateName
    )
      .then(async (response) => {
        // Create Payment with given order id from response

        await CustomerCheckoutsService.createPayment({
          paymentId     : response.data.order.payments[0].id,
          orderId       : response.data.order.id,
          paymentMethod : selectedPaymentMethod
        })

        pageUtils.setAlertMsg('Successfully create an order!', ALERT_TYPES.SUCCESS)

        // Redirect to Order Details page once Order & Payment succesfully created
        history.push(`/app/customer-orders/${response.data.order.id}`)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => setIsOrderProcessing(false))
  }

  const handleClickVoucherApply = async (voucher) => {
    toggleVoucherModal()
    // Check if user has selected paymentMethod and shipment.selected_shipment_method
    const voucherErrors = CustomerCheckoutsService.validateApplyVoucher(
      selectedAddress,
      selectedPaymentMethod,
      selectedShipmentMethod
    )
    if (voucherErrors) {
      alert(voucherErrors)
      return
    }

    // Generate backend Shipment model after validating
    const shipment = CustomerCheckoutsService.generateShipmentModel(shipmentOptions, selectedShipmentMethod)
    await CustomerOrdersApiV2.applyVoucher({
      customer_id         : id,
      code                : voucher.code,
      shipping_address_id : selectedAddress.id,
      payment_method_id   : selectedPaymentMethod.id,
      shipment            : shipment,
      cart_item_ids       : cartItemIds
    })
      .then((response) => {
        const voucherDiscount = response.data
        const isDiscounted    = CustomerCartVoucherService.isDiscounted(voucherDiscount)

        if (isDiscounted) {
          setVoucher(voucher)
          setVoucherDiscount(voucherDiscount)
          return
        }

        setVoucherModalOpen((prevState) => !prevState)
        alert(
          [
            'Your Order does not meet the requirements of the Voucher.\n\n',
            'Please check the Voucher description.'
          ].join('')
        )

      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))

  }

  const handleClickVoucherRemove = () => {
    setVoucher(undefined)
    setVoucherDiscount(undefined)
  }

  const [ voucherModalOpen, setVoucherModalOpen ] = useState(false)
  const toggleVoucherModal                        = () => {
    setVoucherModalOpen((prevState) => !prevState)
  }

  const handleChangeAffiliateName = (event) => {
    const affiliateName = event.target.value
    setSelectedAffiliateName(affiliateName)
  }

  return (
    <>
      <TitlePage mainTitle={ 'Customer Order' } subTitle='Create' />
      <Row>
        { /* Left Col */ }
        <Col md='6'>
          { /* Address */ }
          <CustomerCheckoutSection
            header='Address'
            isEditButtonVisible={ true }
            handleClickEdit={ () => setIsAddressModalOpen(true) }
          >
            {
              selectedAddress && !isAddressLoading
                ? (
                  <CustomerAddressCard
                    address={ selectedAddress }
                    isLoading={ isAddressLoading }
                  />
                )
                :
                <Alert color='info'><i className='fas fa-info-circle mr-2'/>You do not have any address saved. Please add an address to checkout.</Alert>
            }

            <UserAddressModal
              toggle={ setIsAddressModalOpen }
              isModalOpen={ isAddressModalOpen }
              userId={ userId }
              addresses={ addresses }
              selectedAddress={ selectedAddress }
              handleSelectAddress={ handleSelectAddress }
            />
          </CustomerCheckoutSection>

          { /* Shipment */ }
          {
            shipmentOptions && (
              <CheckoutShipmentSection
                isLoading={ isShipmentLoading }
                shipment={ shipmentOptions }
                selectedShipmentMethod={ selectedShipmentMethod }
                handleClickShippingMethod={ handleClickShippingMethod }
                handleClickInsuranceCheckbox={ handleClickInsuranceCheckbox }
              />
            )
          }


          { /* Payment */ }
          <CustomerCheckoutSection
            header='Payment Method'
            isEditButtonVisible={ true }
            handleClickEdit={ () => setIsPaymentModalOpen(true) }
          >
            <CustomerCheckoutPaymentMethodCard
              paymentMethod={ selectedPaymentMethod }
              handleClickChooseButton={ () => setIsPaymentModalOpen(true) }
            />

            <Modal
              scrollable
              centered
              isOpen={ isPaymentModalOpen }
              toggle={ () => setIsPaymentModalOpen(!isPaymentModalOpen) }
              className='mx-auto my-auto'
            >
              <ModalBody>
                <div className='d-flex align-items-center mb-4'>
                  <i className='fas fa-chevron-left'
                    onClick={ () => setIsPaymentModalOpen(false) }
                  />
                  <h4 className='mx-auto mb-0'>
                    { 'P A Y M E N T' }
                  </h4>
                </div>
                { /* { modalChildren.body } */ }
                <CustomercCheckoutPaymentOptions
                  paymentOptions={ paymentOptions }
                  selectedPaymentMethod={ selectedPaymentMethod ? selectedPaymentMethod.id : -1 }
                  handleSelectPayment={ handleSelectPayment }
                />
              </ModalBody>
            </Modal>
          </CustomerCheckoutSection>
        </Col>

        { /* Right Col */ }
        <Col>

          { /* Voucher */ }
          <CustomerCheckoutSection
            header='Voucher'
            isEditButtonVisible={ false }
          >
            <Card body className='shadow'>
              <div className='d-flex justify-content-between align-items-center'>
                {
                  voucher
                    ? (
                      <>
                        <span>
                          <img src={ `${WEB_URL_CONSTANTS.HOMEPAGE}/icons/voucher.svg` } className={ 'mr-3' } />
                          <span className='font-weight-bold'>
                            { voucher.code }
                          </span>
                        </span>
                        <Button
                          color='primary'
                          outline
                          size={ 'sm' }
                          onClick={ handleClickVoucherRemove }
                        >
                          { 'Remove' }
                        </Button>
                      </>
                    )
                    : <>
                      <span className='font-weight-bold'>Pilih Voucher</span>
                      <Button
                        color='primary'
                        outline
                        size={ 'sm' }
                        onClick={ toggleVoucherModal }
                      >
                        { 'Choose Voucher' }
                      </Button>
                    </>
                }

              </div>
            </Card>

            <CustomerCheckoutVoucherModal
              isOpen={ voucherModalOpen }
              toggle={ toggleVoucherModal }
              handleClickVoucherApply={ handleClickVoucherApply }
            />

          </CustomerCheckoutSection>

          { /* Summary */ }
          <CustomerCheckoutSection
            header='Summary'
            isEditButtonVisible={ false }
          >
            {
              shipmentOptions && (
                <CustomerCheckoutTotalSection
                  isLoading={ isShipmentLoading }
                  order={ checkoutOrder }
                />
              )
            }

          </CustomerCheckoutSection>

          <CustomerCheckoutAffiliateNameField
            selectedAffiliateName={ selectedAffiliateName }
            onChangeAffiliateName={ handleChangeAffiliateName }
          />

          <Row noGutters>
            {
              selectedPaymentMethod && (
                <Alert color='info' className='small'><i className='fa fa-info-circle mr-2'/>{ selectedPaymentMethod.description }</Alert>
              )
            }
            <LoaderButton
              className='flex-fill mt-3'
              color='primary'
              text={ 'Create Order' }
              isLoading={ isOrderProcessing }
              onClick={ () => handleClickCreateOrder() }
            />
          </Row>
        </Col>
      </Row>
    </>
  )
}

CustomerCheckoutOrderCreatePage.propTypes    = propTypes
CustomerCheckoutOrderCreatePage.defaultProps = defaultProps

export default CustomerCheckoutOrderCreatePage
